import { Box, Card, Tooltip, Typography, useTheme } from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import GetAppIcon from '@mui/icons-material/GetApp';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import toast from 'react-hot-toast';
import { mindtimeWazuhVulnerabilitiesApi } from 'src/api/mindtime/wazuhVulnerabilitiesApi';
import { Button, Table } from 'src/common';
import type {
  WazuhVulnerabilitiesAgentSummaryData,
  WazuhVulnerabilitiesListQuery,
  WazuhVulnSeverityBucket,
  WazuhVulnerabilityItem,
} from 'src/types/mindtime/wazuhVulnerabilities';
import type { Column, TableData } from 'src/types/table';
import logger from 'src/utils/logger';
import {
  EdrDataCard,
  EdrDataToolbar,
  EdrDateTime,
  EdrSeverityPill,
  EdrStatusPill,
  edrMutedStyle,
  type EdrToolbarFilter,
} from '../../shared/edrPrimitives';
import {
  SecurityDashboardCard,
  securitySecurityScoreCardPadding,
  securitySecurityScoreCardSx,
  securitySecurityScoreHeaderTitleStyle,
  securitySecurityScoreImpactBoxSx,
} from '../../shared/securityDashboardCard';
import { useSecurityDashboardGrid } from '../../shared/useSecurityDashboardGrid';
import {
  getVulnerabilityId,
  toFilterListLabel,
  toVulnerabilityTitle,
  vulnerabilitySeverityToLevel,
  vulnerabilityStatusToTone,
} from '../../vulnerability-detection/vulnerabilityDetailModel';
import { COLOR_BLUE, COLOR_RED } from './shared/endpointTableTab';

const PAGE_SIZE = 25;
const EMPTY_BUCKET: WazuhVulnSeverityBucket = { open: 0, closed: 0, total: 0 };

const SEVERITY_OPTIONS = [
  { label: 'Critical', value: 'critical' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

const STATUS_OPTIONS = [
  { label: 'Open', value: 'open' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Resolved', value: 'resolved' },
];

type Filters = { severity?: string; status?: string; packageName?: string };

const DEFAULT_FILTERS: Filters = { status: 'open' };

const formatNumber = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

const buildListQuery = (
  systemWazuhAgentId: string,
  page: number,
  pageSize: number,
  search: string,
  filters: Filters
): WazuhVulnerabilitiesListQuery => ({
  systemWazuhAgentId,
  page,
  pageSize,
  ...(search.trim() ? { search: search.trim() } : {}),
  ...(filters.severity ? { severity: filters.severity } : {}),
  ...(filters.status ? { status: filters.status } : {}),
  ...(filters.packageName ? { packageName: filters.packageName } : {}),
});

const VulnStatColumn: FC<{
  label: string;
  open: number;
  total: number;
  resolved: number;
  showDivider: boolean;
}> = ({ label, open, total, resolved, showDivider }) => (
  <Box
    style={{
      minWidth: 0,
      padding: '0 20px',
      borderLeft: showDivider ? '1px solid #EDF2F7' : 'none',
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 10 }}>
      <Typography
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.4px',
          color: '#A0AEC0',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
      <ImportExportIcon style={{ fontSize: 16, color: COLOR_BLUE, flexShrink: 0 }} />
    </Box>
    <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
      <Typography style={{ fontSize: 26, fontWeight: 700, color: COLOR_RED, lineHeight: 1.1 }}>
        {formatNumber(open)}
      </Typography>
      <EdrStatusPill tone="high" label="Open" dot={false} />
    </Box>
    <Box display="flex" alignItems="center" style={{ gap: 6, flexWrap: 'wrap' }}>
      <EdrStatusPill tone="neutral" label={`${formatNumber(total)} Total`} />
      <EdrStatusPill tone="success" label={`${formatNumber(resolved)} Resolved`} />
    </Box>
  </Box>
);

const RemediationProgressCard: FC<{
  percent: number;
  addressed: number;
  total: number;
}> = ({ percent, addressed, total }) => {
  const primaryColor = useTheme().palette.primary.main;

  return (
    <Card
      sx={securitySecurityScoreCardSx}
      style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}
    >
      <Box sx={{ padding: securitySecurityScoreCardPadding }}>
        <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 8 }}>
          <Box display="flex" alignItems="center" style={{ color: primaryColor, lineHeight: 0 }}>
            <ShowChartIcon style={{ fontSize: 18 }} />
          </Box>
          <Typography
            component="span"
            style={{ ...securitySecurityScoreHeaderTitleStyle, color: primaryColor }}
          >
            Remediation Progress
          </Typography>
        </Box>
        <Typography
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 24,
            fontWeight: 700,
            lineHeight: 1.1,
            color: '#1A202C',
            marginBottom: 8,
          }}
        >
          {formatNumber(percent)}%
        </Typography>
        <Box sx={securitySecurityScoreImpactBoxSx} display="flex" alignItems="center">
          <Typography
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 11,
              lineHeight: '16px',
              color: '#4A5568',
            }}
          >
            {formatNumber(addressed)} of {formatNumber(total)} addressed
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

type EndpointVulnerabilitiesTabProps = {
  systemWazuhAgentId: string;
};

const EndpointVulnerabilitiesTab: FC<EndpointVulnerabilitiesTabProps> = ({
  systemWazuhAgentId,
}) => {
  const { mainWithRailColumns, isMdDown, isSmDown, portfolioKpiColumns } = useSecurityDashboardGrid();
  const agentId = systemWazuhAgentId.trim();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [packageFacets, setPackageFacets] = useState<string[]>([]);
  const [packageSearch, setPackageSearch] = useState('');
  const [listLoading, setListLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState<WazuhVulnerabilitiesAgentSummaryData | null>(null);
  const [items, setItems] = useState<WazuhVulnerabilityItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setPage(0);
      }, 300),
    []
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  useEffect(() => {
    if (!agentId) {
      setSummary(null);
      return undefined;
    }
    let cancelled = false;
    mindtimeWazuhVulnerabilitiesApi
      .getAgentSummary(agentId)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'EndpointVulnerabilitiesTab.summary');
          setSummary(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  useEffect(() => {
    if (!agentId) {
      setItems([]);
      setTotalRecords(0);
      setListLoading(false);
      return undefined;
    }
    let cancelled = false;
    setListLoading(true);
    mindtimeWazuhVulnerabilitiesApi
      .list(buildListQuery(agentId, page, rowsPerPage, search, filters))
      .then((data) => {
        if (cancelled) return;
        setItems(data?.items ?? []);
        setTotalRecords(data?.totalRecords ?? 0);
        setListLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        logger(error, 'error', 'EndpointVulnerabilitiesTab.list');
        setItems([]);
        setTotalRecords(0);
        setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, page, rowsPerPage, search, filters.severity, filters.status, filters.packageName]);

  useEffect(() => {
    setPackageFacets([]);
    setPackageSearch('');
  }, [agentId]);

  useEffect(() => {
    if (!items.length) return;
    setPackageFacets((prev) => {
      const next = new Set(prev);
      items.forEach((item) => {
        const name = item.packageName?.trim();
        if (name) next.add(name);
      });
      return Array.from(next).sort((a, b) => a.localeCompare(b));
    });
  }, [items]);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        id: getVulnerabilityId(item) || item.cveId,
      })),
    [items]
  );

  const updatedLabel = summary?.meta?.lastSyncedAt
    ? `Updated: ${new Date(summary.meta.lastSyncedAt).toLocaleString()}`
    : 'Updated: Just now';

  const packageOptions = useMemo(() => {
    const query = packageSearch.trim().toLowerCase();
    return packageFacets
      .filter((name) => !query || name.toLowerCase().includes(query))
      .map((name) => ({ value: name, label: toFilterListLabel(name) }));
  }, [packageFacets, packageSearch]);

  const setFilter = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
    setPage(0);
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchInput('');
    setSearch('');
    setPackageSearch('');
    setPage(0);
  };

  const handleExport = () => {
    if (!agentId) return;
    setExporting(true);
    mindtimeWazuhVulnerabilitiesApi
      .export(buildListQuery(agentId, 0, rowsPerPage, search, filters))
      .then(({ blob, filename }) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename?.trim() || 'vulnerabilities-export.csv');
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Export downloaded');
      })
      .catch((error) => {
        logger(error, 'error', 'EndpointVulnerabilitiesTab.export');
        toast.error(error instanceof Error ? error.message : 'Export failed');
      })
      .finally(() => setExporting(false));
  };

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: SEVERITY_OPTIONS,
        value: filters.severity ?? '',
        onChange: (value) => setFilter('severity', value),
      },
      {
        id: 'status',
        label: 'Status',
        options: STATUS_OPTIONS,
        value: filters.status ?? '',
        onChange: (value) => setFilter('status', value),
      },
      {
        id: 'package',
        label: 'Package',
        variant: 'searchable' as const,
        value: filters.packageName ?? '',
        searchableOptions: packageOptions,
        searchableOnSearch: setPackageSearch,
        onChange: (value: string) => {
          setFilter('packageName', value);
          setPackageSearch('');
        },
      },
    ],
    [filters, packageOptions]
  );

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'title',
        label: 'Event Name',
        width: '44%',
        render: (row) => {
          const item = row as unknown as WazuhVulnerabilityItem;
          const title = item.title?.trim() || '—';
          const cveId = item.cveId?.trim() || '—';
          return (
            <Tooltip title={title !== '—' ? title : ''} placement="top" enterDelay={300}>
              <Box
                style={{
                  display: 'block',
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                <Typography
                  noWrap
                  component="div"
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#1E293B',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  noWrap
                  component="div"
                  style={{
                    ...edrMutedStyle,
                    fontSize: 11,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {cveId}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        id: 'packageName',
        label: 'Package',
        width: '20%',
        render: (row) => {
          const packageName =
            (row as unknown as WazuhVulnerabilityItem).packageName?.trim() || '—';
          return (
            <Tooltip title={packageName !== '—' ? packageName : ''} placement="top" enterDelay={300}>
              <Typography noWrap style={{ fontSize: 12, color: '#334155', maxWidth: '100%' }}>
                {packageName}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        id: 'severity',
        label: 'Severity',
        width: '12%',
        render: (row) => {
          const item = row as unknown as WazuhVulnerabilityItem;
          return (
            <EdrSeverityPill
              level={vulnerabilitySeverityToLevel(item.severity)}
              label={toVulnerabilityTitle(item.severity)}
            />
          );
        },
      },
      {
        id: 'status',
        label: 'Status',
        width: '14%',
        render: (row) => {
          const item = row as unknown as WazuhVulnerabilityItem;
          return (
            <EdrStatusPill
              tone={vulnerabilityStatusToTone(item.status)}
              label={toVulnerabilityTitle(item.status)}
            />
          );
        },
      },
      {
        id: 'detectedAt',
        label: 'Detected',
        width: '12%',
        render: (row) => (
          <EdrDateTime value={(row as unknown as WazuhVulnerabilityItem).detectedAt} />
        ),
      },
    ],
    []
  );

  const critical = summary?.critical ?? EMPTY_BUCKET;
  const high = summary?.high ?? EMPTY_BUCKET;
  const low = summary?.low ?? EMPTY_BUCKET;

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 8, minWidth: 0 }}>
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: mainWithRailColumns,
          gap: 16,
          alignItems: 'stretch',
          minWidth: 0,
        }}
      >
        <SecurityDashboardCard
          title="Vulnerabilities"
          icon={<BugReportIcon style={{ fontSize: 18 }} />}
          headerRight={
            <Typography style={{ fontSize: 11, color: '#A0AEC0' }}>{updatedLabel}</Typography>
          }
          style={{ height: '100%', minWidth: 0 }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: portfolioKpiColumns, py: 2 }}>
            <VulnStatColumn
              label="Total Vulnerabilities"
              open={summary?.open ?? 0}
              total={summary?.total ?? 0}
              resolved={summary?.closed ?? 0}
              showDivider={false}
            />
            <VulnStatColumn
              label="Critical"
              open={critical.open}
              total={critical.total}
              resolved={critical.closed}
              showDivider={!isSmDown}
            />
            <VulnStatColumn
              label="High"
              open={high.open}
              total={high.total}
              resolved={high.closed}
              showDivider={!isMdDown}
            />
            <VulnStatColumn
              label="Low"
              open={low.open}
              total={low.total}
              resolved={low.closed}
              showDivider={!isMdDown}
            />
          </Box>
        </SecurityDashboardCard>

        <RemediationProgressCard
          percent={summary?.remediationPercent ?? 0}
          addressed={summary?.addressed ?? 0}
          total={summary?.total ?? 0}
        />
      </Box>

      <EdrDataCard
        toolbar={(
          <EdrDataToolbar
            searchValue={searchInput}
            onSearchChange={(value) => {
              setSearchInput(value);
              debouncedSetSearch(value);
            }}
            searchPlaceholder="Search vulnerabilities…"
            filters={toolbarFilters}
            onReset={handleReset}
            trailing={(
              <Button
                variant="outlined"
                color="primary"
                startIcon={<GetAppIcon />}
                style={{ textTransform: 'none' }}
                loading={exporting}
                onClick={handleExport}
              >
                Export
              </Button>
            )}
          />
        )}
      >
        <Table
          columns={columns}
          data={rows as unknown as TableData[]}
          loading={listLoading}
          showPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalRecords}
          onPageChange={setPage}
          onRowsPerPageChange={(next) => {
            setRowsPerPage(next);
            setPage(0);
          }}
          TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          MUITableProps={{ sx: { tableLayout: 'fixed', width: '100%' } }}
        />
      </EdrDataCard>
    </Box>
  );
};

export default EndpointVulnerabilitiesTab;
